// Deterministic, no-LLM narrative used until the AI summary endpoint responds
// (and as a permanent fallback if it fails). Everything here is derived
// straight from the CDI numbers already on the page, with no interpretation.

import { CDI_CLASSES } from "./reportConfig";

function pctSum(pctByClass) {
  return CDI_CLASSES.reduce((total, cls) => total + (pctByClass[cls.key] || 0), 0);
}

function fmt(value) {
  return (Number(value) || 0).toFixed(1);
}

const FORECAST_KEYS = ["Below Normal", "Normal to Below", "Normal to Above", "Above Normal"];

/**
 * Build the executive-summary fallback text: a monitoring paragraph and an
 * outlook paragraph.
 *
 * @param {object} args
 * @param {object|null} args.cdiOverview `cdi` payload from `data=cdi_overview`.
 * @param {string} args.countryName
 * @param {"national"|"regional"} args.scope
 * @param {{label: string}|null} args.adminOne
 * @param {object|null} [args.forecast] `forecast_summary` from `data=longrange_forecast`.
 * @returns {string}
 */
export function buildExecutiveSummary({ cdiOverview, countryName, scope, adminOne, forecast }) {
  const pct = cdiOverview?.percentage_area;

  const monitoring = pct
    ? (() => {
        const affected = pctSum(pct);
        const noDrought = Math.max(0, 100 - affected);
        const spread = affected > 50 ? "widespread" : "localized";
        const regions = (cdiOverview?.overview || []).slice(0, 3).map((r) => r.admin_one_name);
        const bits = [
          `About ${fmt(noDrought)}% of ${countryName} shows no CDI drought signal this period, ` +
            `while ${fmt(affected)}% is affected: ${fmt(pct.Alert)}% in Alert, ` +
            `${fmt(pct.Warning)}% in Warning and ${fmt(pct.Watch)}% in Watch.`,
          `Conditions are ${spread} at the national scale.`,
        ];
        if (regions.length) bits.push(`The most affected admin-1 regions are ${regions.join(", ")}.`);
        if (scope === "regional" && adminOne) {
          bits.push(`${adminOne.label} is selected for admin-one review; national figures stay visible above.`);
        }
        return bits.join(" ");
      })()
    : `No significant CDI-classified drought is reported for ${countryName} this period.`;

  // Outlook paragraph from the seasonal forecast, where available.
  const fpct = forecast?.percentage_area;
  let outlook = "The seasonal forecast for the coming season is not yet available for this period.";
  if (fpct && FORECAST_KEYS.some((k) => fpct[k] != null)) {
    const dominant = [...FORECAST_KEYS].sort((a, b) => (fpct[b] || 0) - (fpct[a] || 0))[0];
    const season = forecast.forecast_period ? `${forecast.forecast_period} season` : "coming season";
    outlook =
      `The ACMAD long-range forecast for the ${season} leans towards ${dominant.toLowerCase()} ` +
      `rainfall, covering about ${fmt(fpct[dominant])}% of the country. ` +
      (/below/i.test(dominant)
        ? "A drier-than-normal tilt would reinforce the current drought signal into the season ahead."
        : "A wetter or near-normal tilt would ease pressure on water, cropping and pasture if it verifies.");
  }

  return `${monitoring}\n\n${outlook}`;
}

/**
 * Comprehensive fallback analysis for the CDI regional-breakdown panel: national
 * class split, which regions carry the load, how concentrated it is, and the
 * operational reading.
 *
 * @param {object|null} cdiOverview
 * @returns {string}
 */
export function buildCdiExplanation(cdiOverview) {
  const pct = cdiOverview?.percentage_area;
  const totals = cdiOverview?.total_area_per_per_class || {};
  const overview = cdiOverview?.overview || [];
  if (!pct) {
    return "No CDI-classified drought area is reported at admin-1 level for this period.";
  }

  const affected = pctSum(pct);
  const noDrought = Math.max(0, 100 - affected);
  const km2 = (v) => Math.round(Number(v) || 0).toLocaleString("en-US");

  const parts = [
    `Nationally, about ${fmt(noDrought)}% of the country is outside drought, ` +
      `while ${fmt(pct.Watch)}% is in Watch, ${fmt(pct.Warning)}% in Warning and ` +
      `${fmt(pct.Alert)}% in Alert.`,
    `That is roughly ${km2(totals.Watch)} km² in Watch, ${km2(totals.Warning)} km² ` +
      `in Warning and ${km2(totals.Alert)} km² in Alert.`,
  ];

  const withPct = overview.filter((r) => r.percentage_affected != null);
  const ranked = (withPct.length ? withPct : overview).slice(0, 4);
  if (ranked.length) {
    const named = ranked
      .map((r) =>
        r.percentage_affected != null
          ? `${r.admin_one_name} (${Math.round(r.percentage_affected)}% of the region)`
          : r.admin_one_name,
      )
      .join(", ");
    parts.push(`The affected area is led by ${named}.`);
  }

  const concentrated = ranked.length > 0 && affected > 0;
  if (concentrated) {
    const alertRegions = overview.filter((r) => (r.Alert || 0) > 0).map((r) => r.admin_one_name);
    parts.push(
      affected > 50
        ? "Drought is widespread rather than confined to a few regions."
        : "Drought is concentrated in a limited number of regions rather than spread evenly.",
    );
    if (alertRegions.length) {
      parts.push(`Alert-class area is present in ${alertRegions.slice(0, 4).join(", ")}.`);
    }
  }

  parts.push(
    "Watch areas point to early stress and warrant monitoring; Warning areas are " +
      "likely already affecting water supply, planting decisions and pasture; Alert " +
      "areas face strong pressure on crops, livestock and water sources.",
  );
  return parts.join(" ");
}

/**
 * Placeholder analytics note per indicator, used until the AI note arrives.
 * Keyed by the AI block name with the `_note` suffix removed.
 */
export const INDICATOR_FALLBACK_NOTES = {
  outlook:
    "The ACMAD long-range forecast gives the probability that seasonal rainfall " +
    "falls in the below-normal, near-normal or above-normal third of the record, " +
    "while UNWDC classifies how unusual recent wet or dry conditions are against " +
    "climatology on a six-class scale from Driest to Wettest. Read together, they " +
    "frame the weeks and months ahead: a below-normal forecast tilt alongside a " +
    "dry UNWDC signal points to the current drought persisting or deepening into " +
    "the season, whereas a near-normal or wetter tilt would give scope for " +
    "recovery. The implications fall on water availability, the timing of the " +
    "cropping calendar and livestock condition in the most exposed regions.",
  cdi_trend:
    "The CDI severity scale runs from Watch, which marks early or emerging stress " +
    "where rainfall, soil moisture or vegetation has just begun to slip below " +
    "normal, through Warning, which marks moderate and established drought with " +
    "likely impacts on water supply, planting and pasture, to Alert, which marks " +
    "severe drought with strong impacts on crops, livestock and water sources. " +
    "Across the recent dekads the combined affected area has largely persisted " +
    "around the existing Watch and Warning zones rather than opening new fronts, " +
    "with the Alert share remaining the smallest but most consequential part of " +
    "the picture. Where the footprint has grown it has done so at the edges of " +
    "areas already stressed, and some Watch ground has hardened into Warning " +
    "rather than easing. If the current trajectory holds, the affected area is " +
    "likely to stay near its present level or expand slowly over the coming weeks " +
    "rather than retreat.",
  spi_trend:
    "SPI expresses rainfall as a standardised departure from the 1981 to 2010 " +
    "average for the same place and season: about minus 1 is moderately dry, minus " +
    "1.5 severely dry and minus 2 or lower extremely dry, with mirrored classes on " +
    "the wet side. The two maps show SPI-3 from different inputs: GPCC gauge data, " +
    "which feeds the CDI and lags the analysis period by one month, and ERA5 " +
    "reanalysis, which is close to real time. Over recent periods both sources " +
    "place a similar share of the country on the dry side of the scale and agree " +
    "on the direction of movement, so the rainfall deficit signal is consistent " +
    "across inputs rather than an artefact of one dataset. Where they differ, the " +
    "ERA5 map is the more current read of conditions on the ground, while the " +
    "GPCC map reflects the rainfall that was actually gauged a month earlier.",
  veg_soil:
    "Soil moisture anomaly measures water in the crop root zone, while fAPAR " +
    "anomaly measures how much light the vegetation canopy is absorbing, its " +
    "greenness and vigour. Both are currently below normal and trending drier. " +
    "Soil moisture usually moves first and vegetation follows one to a few dekads " +
    "later, so the two declining together here signals an established impact rather " +
    "than an early warning still working through. For rain-fed cropping this " +
    "tightens water through germination and early growth and raises yield risk for " +
    "the season; for rangelands it means slower pasture regeneration, weaker " +
    "livestock body condition and growing pressure on pastoral water points.",
};
